<script lang="ts" setup>
import SpotPage from '@/pages/maps/[mapId]/spots/[spotId].vue';
import type { Tables } from '~~/types/database.types.ts';
import {
  useIntersectionObserver,
  useScroll,
  useBreakpoints,
} from '@vueuse/core';
import filter from 'lodash.filter';
import orderBy from 'lodash.orderby';
import uniqBy from 'lodash.uniqby';
import {
  computed,
  defineOgImageComponent,
  onMounted,
  ref,
  useAsyncData,
  useAuth,
  useDialog,
  useHead,
  useMaps,
  useMetrics,
  useNuxtApp,
  useRoute,
  useRouter,
  useStorage,
  useToast,
  watch,
} from '#imports';

// Fetch Map
const route = useRoute();
const mapId = route.params.mapId as string;
const { fetchMapBySlug } = useMaps();
const {
  data: map,
  pending: mapPending,
  refresh: mapRefresh,
} = await useAsyncData(
  'map:{id}',
  (): Promise<Tables<'maps'>> => fetchMapBySlug(mapId),
);
const { currentUser } = useAuth();

const { formatedImageUrl } = useStorage();
const mapImage = computed(() =>
  formatedImageUrl(map.value?.cover_image_url, 2000, 800, 75),
);
const backgroundImage = computed(() => `url(${mapImage.value})`);

// Transform Categories
const spotCategories = computed(() =>
  uniqBy(
    (map.value?.spots || [])
      .flatMap((spot: Tables<'spots'>) => spot.categories || [])
      .filter(Boolean),
    'id',
  ),
);
const spotDestinations = computed(() =>
  uniqBy(
    (map.value?.spots || [])
      .map((spot: Tables<'spots'>) => spot.cities)
      .filter(Boolean),
    'country',
  ),
);
const spotSections = computed(() =>
  uniqBy(
    (map.value?.spots || [])
      .map((spot: Tables<'spots'>) => {
        return spot.section
          ? {
              name: spot.section,
              id: spot.section,
            }
          : null;
      })
      .filter(Boolean),
    'name',
  ),
);
const countries = computed(() => {
  if (!map.value) return [];
  return uniqBy(
    map.value.spots.map((spot: Tables<'spots'>) => spot.cities).filter(Boolean),
    'country',
  );
});

const { trackEvent } = useMetrics();
onMounted(() => {
  if (!map) return;
  trackEvent('viewed_map', {
    map_id: mapId,
    map_name: map.value.title,
    creator_handle: map.value.profiles.mappr_handle,
    creator_id: map.value.user_id,
  });
});

// Setup SEO
useHead(() => ({
  title: `${map.value?.title}`,
  meta: [
    {
      name: 'description',
      content: map.value?.summary,
    },
  ],
}));

defineOgImageComponent('Default', {
  title: map.value?.title,
  description: map.value?.summary,
  imagePath: formatedImageUrl(map.value?.cover_image_url, 1200, 600, 75),
  by: `Map By: @${map.value?.profiles.mappr_handle}`,
});

// User data
const isSupporter = ref(false);
watch(
  currentUser,
  () => {
    isSupporter.value = currentUser.value?.supports?.some(
      (support) => support.creator_id === map.value?.user_id,
    );
  },
  {
    immediate: true,
  },
);

// Animations
const mapView = ref(null);
const mapViewIsVisible = ref(false);
const isFullscreen = ref(false);

useIntersectionObserver(
  mapView,
  ([{ isIntersecting }]) => {
    mapViewIsVisible.value = isIntersecting;
    isFullscreen.value = isIntersecting;
  },
  {
    threshold: 0.8,
  },
);

const { y } = useScroll(window);
// const img = ref(null)
const mapHeader = ref(null);
const mapImageisVisible = ref(false);

useIntersectionObserver(
  mapHeader,
  ([{ isIntersecting }]) => {
    mapImageisVisible.value = isIntersecting;
  },
  { threshold: 0.1, immediate: true },
);

const startY = ref(0);
watch(y, () => {
  if (mapHeader.value && mapImageisVisible.value) {
    const translateY = Math.max(
      startY.value,
      startY.value + (y.value / window.innerHeight) * 30,
    );
    const move = 50 + (translateY * 0.5);
    mapHeader.value.style.backgroundPosition = `center ${move}%`;
  }
});

/*
Map Controls:
Back buttons filters etc.
*/

// Back Button
const router = useRouter();
const handleBackButton = () => {
  const previousPage = window?.document.referrer;
  const currentHost = window?.location.host;
  if (window && previousPage && new URL(previousPage).host === currentHost) {
    router.go(-1);
  } else {
    router.push('/explore');
  }
  router.push('/explore');
};

// Open Share Dialog
const { showError } = useToast();
const openShareDialog = () => {
  if (!map.value) return;
  try {
    openDialog('MPShare', {
      map: map.value,
      title: map.value.title,
      subtitle: 'Share map:',
      version: 'share',
    });
  } catch (e) {
    showError('Error opening share dialog');
  }
};

// Checkout and payments
const { openDialog, setDialogFullscreen } = useDialog();
type PricingTier = 'premium' | 'support';
const pricingTier = ref<PricingTier>('premium');
const openPricingDialog = (tier: PricingTier) => {
  if (!map.value) return;
  pricingTier.value = tier;
  openDialog('MPPricing', {
    isOpen: 'pricingDialogIsOpen',
    creator: map.value.profiles,
    showSupporterTier: map.value.profiles.stripe_account_id,
  });
  setDialogFullscreen(true);
  trackEvent(`${tier}_intent`, {
    map_id: mapId,
    map_name: map.value.title,
    creator_id: map.value.profiles.mappr_handle,
    creator_handle: map.value.profiles.mappr_handle,
  });
};

// Open Collaboration Form
const openCollaborationForm = () => {
  if (!map.value) return;

  trackEvent('collaboration_intent', {
    map_id: mapId,
    map_name: map.value.title,
    creator_handle: map.value.profiles.mappr_handle,
    creator_id: map.value.user_id,
  });
  try {
    if (currentUser.value) {
      openDialog('MPCollaborateForm', {
        creatorId: map.value.profiles.id,
        creatorDisplayName: map.value.profiles.display_name,
      });
    } else {
      openDialog('MPAuth', { startOn: 'login' });
    }
  } catch (e) {
    showError('Error opening collaboration dialog', e);
  }
};

// Map Filters
const categoryFilter = ref<Tables<'categories'>[]>([]);
const sectionFilter = ref<string[]>([]);
const destinationFilter = ref<Tables<'cities'>[]>([]);
const sortFilter = ref<Array<string>>([]);
const sortFilters = ref([
  {
    name: 'Newest',
    id: 'recent_desc',
  },
  {
    name: 'Oldest',
    id: 'recent_asc',
  },
  {
    name: 'A-Z',
    id: 'alpha_asc',
  },
  {
    name: 'Z-A',
    id: 'alpha_desc',
  },
]);
const filteredSpots = computed(() => {
  if (!map.value || !map.value.spots) return;

  let filtered = filter(map.value.spots, (spot) => {
    const categoryMatch =
      categoryFilter.value.length === 0 ||
      (spot.categories && categoryFilter.value.includes(spot.categories.id));

    const sectionMatch =
      sectionFilter.value.length === 0 ||
      sectionFilter.value.includes(spot.section);

    const destinationMatch =
      destinationFilter.value.length === 0 ||
      (spot.cities && destinationFilter.value.includes(spot.cities.id));

    return categoryMatch && sectionMatch && destinationMatch;
  });

  switch (sortFilter.value[0]) {
    case 'recent_desc':
      filtered = orderBy(filtered, ['created_at'], ['desc']);
      break;
    case 'recent_asc':
      filtered = orderBy(filtered, ['created_at'], ['asc']);
      break;
    case 'alpha_asc':
      filtered = orderBy(filtered, ['name'], ['asc']);
      break;
    case 'alpha_desc':
      filtered = orderBy(filtered, ['name'], ['desc']);
      break;
    default:
      break;
  }
  return filtered;
});

// Spot navigation
const hasOpenSpot = computed(() => route.params.spotId !== undefined);

const breakpoints = useBreakpoints({
  mobile: 920,
});

const isMobile = breakpoints.smaller('mobile');

const spotVisible = computed(() => route.params.spotId !== undefined);

const nuxtApp = useNuxtApp();
const isHydrated = ref(false);

onMounted(() => {
  isHydrated.value = true;
});

const closeSpot = () => {
  if (isHydrated.value) {
    router.replace({ path: `/maps/${mapId}` });
  } else {
    router.push({ path: `/maps/${mapId}` });
  }
};

const openSpot = (spotId: string) => {
  if (isHydrated.value) {
    // Client-side navigation - update URL without full page reload
    router.replace({ path: `/maps/${mapId}/spots/${spotId}` });
  } else {
    // Server-side navigation
    router.push({ path: `/maps/${mapId}/spots/${spotId}` });
  }
};

const currentIndex = computed(() => {
  return filteredSpots.value.findIndex(
    (spot) => spot.id === route.params.spotId,
  );
});

const nextSpot = computed(() => {
  if (currentIndex.value === filteredSpots.value.length - 1) {
    return { path: `/maps/${mapId}` };
  }
  return {
    path: `/maps/${mapId}/spots/${filteredSpots.value[currentIndex.value + 1].id}`,
  };
});

const prevSpot = computed(() => {
  if (currentIndex.value === 0) {
    return { path: `/maps/${mapId}` };
  }
  return {
    path: `/maps/${mapId}/spots/${filteredSpots.value[currentIndex.value - 1]?.id}`,
  };
});

const next = () => {
  resetTimer();
  if (isHydrated.value) {
    router.replace(nextSpot.value);
  } else {
    router.push(nextSpot.value);
  }
};

const prev = () => {
  resetTimer();
  if (isHydrated.value) {
    router.replace(prevSpot.value);
  } else {
    router.push(prevSpot.value);
  }
};

const timerId = ref(null);
const timer = ref(0);
const timerPaused = ref(false);
const stopTimer = () => {
  timerPaused.value = true;
  clearInterval(timerId.value);
  timerId.value = null;
};
const resetTimer = () => {
  timer.value = 0;
};
const startTimer = () => {
  timerPaused.value = false;
  if (!timerId.value && isMobile.value) {
    timerId.value = setInterval(() => {
      timer.value++;
    }, 100);
  }
};
watch(timer, (newValue) => {
  if (!isMobile.value) return;
  if (newValue > 70) {
    resetTimer();
    next();
  }
});

const isSpotOpen = computed(() => route.params.spotId !== undefined);

watch(isSpotOpen, () => {
  if (isSpotOpen.value) {
    startTimer();
  } else {
    stopTimer();
    resetTimer();
  }
});

const mapComponent = ref(null);

// Update your watch for route parameters
watch(
  () => route.params.spotId,
  (newSpotId) => {
    if (newSpotId && mapComponent.value) {
      const spot = filteredSpots.value
        .find((s: Tables<'spots'>) =>
          s.id === newSpotId
          || s.current_slug === newSpotId
        );
      if (spot) {
        mapComponent.value.centerOnSpot(spot.id);
      }
    }
  },
  { immediate: true },
);
const controls = ref<HTMLElement>();
const offsetSpotListAtMobile = ref<string>('0px');
onMounted(() => {
  const mapViewHeight = mapView?.value?.offsetHeight;
  const mapHeaderHeight = mapHeader?.value?.offsetHeight;
  const controlsHeight = controls?.value?.offsetHeight;
  offsetSpotListAtMobile.value = `${mapViewHeight + mapHeaderHeight + controlsHeight}px`;
});
</script>

<template>
  <main class="map-page" v-if="mapPending"/>
  <main :class="['map-page', { 'is-fullscreen': isFullscreen }]" v-else-if="map && !mapPending">
    <div class="header" ref="mapHeader">
      <div class="header-content">
        <div v-if="!currentUser?.hasActiveSubscription" class="offer-banner">
          Get Unlimited Access with Mappr Premium for $5/month!&nbsp;<a style="cursor: pointer;" @click="openPricingDialog('premium') ">Subscribe to Mappr</a>
        </div>
        <MPButton icon="ri-arrow-left-s-line" size="large" hide-label label="Back" class="back-button" link rounded @click="handleBackButton" />
        <span class="countries">
          <span v-for="(country, idx) in countries" :key="country.id">
            {{ country.country }}{{ countries.length > 1 && idx < countries.length - 1 ? ',' : '' }}&nbsp;
          </span>
        </span>
        <h1 class="title">{{ map.title }}</h1>
        <p class="summary">
          {{ map.summary }}
        </p>
        <MPCreatorTag
          :creator="map.profiles"
          class="creator-tag"
        />
      </div>
    </div>
    <div class="controls" ref="controls">
      <MPCreatorTag
        v-if="!isMobile || (isMobile && !isSpotOpen)"
        :creator="map.profiles"
        class="creator-tag"
      />
      <MPButton
        v-show="!isSupporter && map.profiles.stripe_account_id"
        :label="`Support @${map.profiles.mappr_handle}`"
        class="mobile-support-button"
        version="primary"
        size="medium"
        rounded
        @click="openPricingDialog('support')"
      />
      <MPButton
        label="Share Map"
        icon="ri-share-line"
        version="secondary"
        size="medium"
        :hide-label="isMobile"
        rounded
        @click="openShareDialog()"
      />
      <MPBookmark
        bookmark="map_bookmarks"
        :map-id="map.id"
        rounded
        version="secondary"
        label="Follow"
      />
      <MPButton
        label="Collaborate"
        version="secondary"
        size="medium"
        rounded
        @click="openCollaborationForm()"
      />
    </div>
    <div class="map-view" id="map-view" ref="mapView">
      <ClientOnly>
        <MPMap
          v-if="map"
          ref="mapComponent"
          :locations="filteredSpots"
          gesture-handling="cooperative"
          class="map-container"
          @marker-click="openSpot($event)"
        />
      </ClientOnly>
      <div class="spots-list">
        <h2 v-if="map.profiles.mappr_handle"><span>@{{ map.profiles.mappr_handle }}'s</span> spots</h2>
        <MPViewportSpill show-inline-control class="categories-filter">
          <MPCategoryFilter :categories="spotCategories" v-model="categoryFilter"/>
        </MPViewportSpill>
        <ul v-if="!isMobile || (isMobile && !isSpotOpen)">
          <li v-for="spot in filteredSpots.filter(spot => spot.visibility === 'public')" :key="spot.id">
            <MPSpotCard show-tier show-destination :spot="spot" version="small" actions/>
          </li>
          <li v-for="spot in filteredSpots.filter(spot => spot.visibility === 'authenticated')" :key="spot.id">
            <MPSpotCard show-tier show-destination :spot="spot" version="small" actions/>
          </li>
          <li v-for="spot in filteredSpots.filter(spot => spot.visibility === 'subscriber')" :key="spot.id">
            <MPSpotCard show-tier show-destination :spot="spot" version="small" actions/>
          </li>
          <li v-for="spot in filteredSpots.filter(spot => spot.visibility === 'supporter')" :key="spot.id">
            <MPSpotCard show-tier show-destination :spot="spot" version="small" actions/>
          </li>
          <li v-if="!currentUser" class="unlock-cta public-cta">
            <small>Showing {{ filteredSpots.length }} out of {{ map.spots_count }} Spots</small>
            <strong>Join Mappr</strong>
            <p>Discover curated lists of the best spots, handpicked by experts and creators</p>
            <MPButton
              label="Sign Up"
              version="secondary"
              rounded
              size="large"
              icon-color="var(--color-rose-500)"
              background-color="var(--color-white)"
              @click="openDialog('MPAuth', { startOn: 'signup' })"
            />
            <div class="bg-logo">
            <svg width="159" height="187" viewBox="0 0 159 187" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M92.1364 0.0570068C91.0705 0.0570068 89.8522 0.0570069 88.7863 0.361565L43.1026 12.8484C42.1889 13.153 41.4275 13.4576 40.6661 13.9144C39.9047 14.3712 39.1433 14.9803 38.5342 15.7417C37.9251 16.5031 37.316 17.2645 36.7069 18.0259C36.25 18.7873 35.7932 19.701 35.6409 20.6147L1.37816 148.834C0.921322 150.204 0.769043 151.575 0.769043 152.945C0.769043 154.163 0.921322 155.534 1.22588 156.752C1.53044 157.818 1.98727 158.884 2.59639 159.798C3.20551 160.559 3.9669 161.32 4.88058 161.777L57.7214 185.533C59.0919 186.142 60.6147 186.446 62.1375 186.294C63.6603 186.142 65.0308 185.685 66.0967 184.771C67.315 183.858 68.3809 182.64 69.2946 181.269C70.2083 179.594 70.8174 177.919 70.9697 176.092L73.863 156.448L106.451 166.346C107.669 166.802 109.039 166.803 110.41 166.65C111.628 166.498 112.846 165.889 113.912 165.127C114.978 164.366 115.892 163.3 116.501 162.082C117.11 160.711 117.567 159.341 117.719 157.818L118.938 141.981L148.784 147.311C150.002 147.463 151.221 147.463 152.439 147.159C153.505 146.854 154.571 146.397 155.484 145.636C156.398 144.874 157.007 143.961 157.616 143.047C158.073 141.981 158.378 140.763 158.378 139.545V28.8377C158.378 28.2286 158.226 27.6195 157.769 27.1627C157.312 26.7058 156.855 26.4013 156.246 26.249C155.484 26.0967 154.875 25.9444 154.114 26.0967C153.2 26.249 152.287 26.4013 151.525 26.7058L127.313 36.2994L128.531 19.8533C128.683 19.0919 128.531 18.3305 128.074 17.7214C127.77 17.1123 127.161 16.6554 126.551 16.3509C125.79 16.0463 125.029 15.894 124.115 15.894C123.049 15.894 122.135 16.0463 121.222 16.5031L93.5069 25.7922L96.5525 5.53905C96.7048 4.62538 96.5525 3.7117 96.248 2.79803C95.9434 2.03663 95.3343 1.42752 94.7252 0.970681C93.8115 0.361565 93.0501 0.0570068 92.1364 0.0570068ZM127.313 36.1471L119.09 141.829L103.862 139.088C103.101 138.935 102.339 138.631 101.882 138.174C101.273 137.717 100.816 137.108 100.359 136.347C99.9027 135.585 99.7504 134.824 99.5981 134.063C99.4458 133.149 99.4458 132.235 99.5981 131.474L111.78 46.1975C111.933 45.5884 112.085 44.9793 112.39 44.5225C112.694 43.9134 113.151 43.3042 113.455 42.8474C113.912 42.3906 114.369 41.7814 114.978 41.4769C115.587 41.0201 116.197 40.7155 116.806 40.4109L127.313 36.1471ZM93.5069 25.7922L74.0152 156.448L57.4168 151.422C56.6554 151.118 55.894 150.661 55.2849 150.052C54.6758 149.443 54.219 148.529 53.7621 147.768C53.4576 146.854 53.153 145.788 53.153 144.874C53.0007 143.808 53.153 142.742 53.3053 141.676L75.0812 36.7562C75.2335 35.9948 75.538 35.3857 75.8426 34.6243C76.2994 33.8629 76.7563 33.2538 77.2131 32.6447C77.8222 32.0356 78.4313 31.4265 79.0404 30.9696C79.6496 30.5128 80.4109 30.2082 81.1723 29.9037L93.5069 25.7922Z" fill="black"/>
            </svg>
            </div>
          </li>
          <li v-if="currentUser && !currentUser.hasActiveSubscription" class="unlock-cta authenticated-cta">
            <small>Showing {{ filteredSpots.length }} out of {{ map.spots_count }} Spots</small>
            <strong>Mappr Premium</strong>
            <p>Unlock thousands more spots and exclusive recommendations by subscribing, while supporting creators at the same time</p>
            <MPButton
              label="Go Premium"
              version="primary"
              rounded
              background-color="var(--color-white)"
              icon-color="var(--color-green-600)"
              size="large"
              @click="openPricingDialog('premium')"
            />
            <div class="bg-logo bg-logo-green">
              <svg width="159" height="187" viewBox="0 0 159 187" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M92.1364 0.0570068C91.0705 0.0570068 89.8522 0.0570069 88.7863 0.361565L43.1026 12.8484C42.1889 13.153 41.4275 13.4576 40.6661 13.9144C39.9047 14.3712 39.1433 14.9803 38.5342 15.7417C37.9251 16.5031 37.316 17.2645 36.7069 18.0259C36.25 18.7873 35.7932 19.701 35.6409 20.6147L1.37816 148.834C0.921322 150.204 0.769043 151.575 0.769043 152.945C0.769043 154.163 0.921322 155.534 1.22588 156.752C1.53044 157.818 1.98727 158.884 2.59639 159.798C3.20551 160.559 3.9669 161.32 4.88058 161.777L57.7214 185.533C59.0919 186.142 60.6147 186.446 62.1375 186.294C63.6603 186.142 65.0308 185.685 66.0967 184.771C67.315 183.858 68.3809 182.64 69.2946 181.269C70.2083 179.594 70.8174 177.919 70.9697 176.092L73.863 156.448L106.451 166.346C107.669 166.802 109.039 166.803 110.41 166.65C111.628 166.498 112.846 165.889 113.912 165.127C114.978 164.366 115.892 163.3 116.501 162.082C117.11 160.711 117.567 159.341 117.719 157.818L118.938 141.981L148.784 147.311C150.002 147.463 151.221 147.463 152.439 147.159C153.505 146.854 154.571 146.397 155.484 145.636C156.398 144.874 157.007 143.961 157.616 143.047C158.073 141.981 158.378 140.763 158.378 139.545V28.8377C158.378 28.2286 158.226 27.6195 157.769 27.1627C157.312 26.7058 156.855 26.4013 156.246 26.249C155.484 26.0967 154.875 25.9444 154.114 26.0967C153.2 26.249 152.287 26.4013 151.525 26.7058L127.313 36.2994L128.531 19.8533C128.683 19.0919 128.531 18.3305 128.074 17.7214C127.77 17.1123 127.161 16.6554 126.551 16.3509C125.79 16.0463 125.029 15.894 124.115 15.894C123.049 15.894 122.135 16.0463 121.222 16.5031L93.5069 25.7922L96.5525 5.53905C96.7048 4.62538 96.5525 3.7117 96.248 2.79803C95.9434 2.03663 95.3343 1.42752 94.7252 0.970681C93.8115 0.361565 93.0501 0.0570068 92.1364 0.0570068ZM127.313 36.1471L119.09 141.829L103.862 139.088C103.101 138.935 102.339 138.631 101.882 138.174C101.273 137.717 100.816 137.108 100.359 136.347C99.9027 135.585 99.7504 134.824 99.5981 134.063C99.4458 133.149 99.4458 132.235 99.5981 131.474L111.78 46.1975C111.933 45.5884 112.085 44.9793 112.39 44.5225C112.694 43.9134 113.151 43.3042 113.455 42.8474C113.912 42.3906 114.369 41.7814 114.978 41.4769C115.587 41.0201 116.197 40.7155 116.806 40.4109L127.313 36.1471ZM93.5069 25.7922L74.0152 156.448L57.4168 151.422C56.6554 151.118 55.894 150.661 55.2849 150.052C54.6758 149.443 54.219 148.529 53.7621 147.768C53.4576 146.854 53.153 145.788 53.153 144.874C53.0007 143.808 53.153 142.742 53.3053 141.676L75.0812 36.7562C75.2335 35.9948 75.538 35.3857 75.8426 34.6243C76.2994 33.8629 76.7563 33.2538 77.2131 32.6447C77.8222 32.0356 78.4313 31.4265 79.0404 30.9696C79.6496 30.5128 80.4109 30.2082 81.1723 29.9037L93.5069 25.7922Z" fill="black"/>
              </svg>
            </div>
          </li>
        </ul>
      </div>
      <section class="spot-wrapper" v-if="hasOpenSpot">
        <ul class="spot-indicator">
          <li
            v-for="(spot, idx) in filteredSpots"
            :key="spot.id"
            :style="`width: ${filteredSpots.length / 100}`"
          >
            <span
              :class="['indicator', {
                'active': spot.id === route.params.spotId,
                'viewed': idx <= currentIndex,
                'paused': timerPaused
              }]"
            />
          </li>
        </ul>
        <template v-if="isHydrated && route.params.spotId">
          <SpotPage
            @prev="prev"
            @next="next"
            @close="closeSpot"
            @pause-timer="stopTimer"
            @resume-timer="startTimer"
            @reset-timer="resetTimer"
            @subscribe="openPricingDialog($event)"
          />
        </template>
        <template v-else-if="!isHydrated && route.params.spotId">
          <NuxtPage
            @prev="prev"
            @next="next"
            @close="closeSpot"
            @pause-timer="stopTimer"
            @resume-timer="startTimer"
            @reset-timer="resetTimer"
            @subscribe="openPricingDialog($event)"
          />
        </template>
      </section>
    </div>
  </main>
</template>

<style lang="postcss" scoped>
@import 'assets/css/layouts.pcss';
@import 'assets/css/breakpoints.pcss';
@import 'assets/css/typography.pcss';
@import 'assets/css/scrollbar.pcss';

/*
Base Layout
-------
*/
$mapheightmobile: 60vh;
$mapheightdesktop: calc(100vh - 6.5rem);

.map-page {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 8100;
  display: grid;
  background-color: white;
  grid-template-columns: auto;
  grid-template-rows:
    min-content
    $mapheightmobile
    min-content;
  grid-template-areas:
    "header"
    "mapview"
    "controls";

  @mixin screen md {
    position: relative;
    margin-top: -4.25rem;
    z-index: 100;
    grid-template-rows:
      min-content
      min-content
      $mapheightdesktop;
    grid-template-areas:
      "header"
      "controls"
      "mapview";
    grid-template-columns: auto;
  }
}

.header {
  grid-area: header;
}

.back-button {
  @mixin screen md {
    display: none !important;
  }
}

.controls {
  grid-area: controls;
  heightion: fit-content;
  @mixin layout-wrapper-wide;
  transition: all 0.3s ease-in-out;
}

.map-view {
  grid-area: mapview;
  background-color: var(--color-slate-50);
  /* position: relative; */
  @mixin layout-wrapper-wide;
  transition: all 0.3s ease-in-out;
}

.spots-list {
  background: white;
  position: absolute;
  top: v-bind(offsetSpotListAtMobile);
}

.spot-wrapper {
  z-index: 8200; /* over map page */
  position: fixed;
  width: 100%;
  height: 100vh;
  top: 0;
  background-color: var(--color-slate-900);
  @mixin screen md {
    position: absolute;
    background-color: transparent;
    border-radius: 1rem;
    top: 0;
    left: 1rem;
  }
}

.map-page.is-fullscreen {
  @mixin screen md {
    .controls {
      max-width: calc(100% - 2rem);
      transition: all 0.3s ease-in-out;
    }
    .map-view {
      max-width: calc(100% - 2rem);
      transition: all 0.3s ease-in-out;
    }
    .controls .creator-tag {
      position: relative;
      visibility: visible;
      opacity: 1;
      transition: all 0.25s ease-in-out;
    }
  }
}

/*
Header
------
*/
.header {
  background-color: white;
  grid-area: header;
  display: flex;
  box-shadow: var(--shadow-md);

  @mixin screen md {
    color: var(--color-slate-50);
    flex-direction: column;
    align-items: flex-start;
    padding: 14rem 0 10rem;
    background-color: var(--color-slate-800);
    background-image: v-bind(backgroundImage);
    background-image:
      linear-gradient(
        320deg,
        rgba(0, 0, 0, 0) 30%,
        rgba(0, 0, 0, 0.6) 60%,
        rgba(0, 0, 0, 0.8) 100%
      ),
      v-bind(backgroundImage);
    background-repeat: no-repeat;
    background-position: center 50%;
    background-size: cover;
  }

  .header-content {
    display: flex;
    @mixin screen md {
      display: grid;
      @mixin layout-wrapper-wide;
    }
  }

  .title {
    @mixin typeface-h3;
    @mixin screen md {
      @mixin typeface-h1;
      margin: 1rem 0 0;
      max-width: 70%;
    }
    @mixin screen xxl {
      @mixin typeface-title;
      max-width: 50%;
    }
  }

  .countries {
    display: none;
    @mixin screen md {
      display: block;
      color: var(--color-rose-500);
      @mixin typeface-regular;
    }
  }

  .creator-tag {
    display: none;
    @mixin screen md {
      margin-top: 0.5rem;
      display: flex;
    }
  }

  .offer-banner {
    display: none;
    @mixin screen md {
      display: block;
      position: absolute;
      top: 5rem;
      left: 0;
      width: 100vw;
      height: fit-content;
      align-items: center;
      justify-content: center;
      @mixin typeface-regular;
      z-index: 100;
      background: rgba(255, 255, 255, 0.60);
      backdrop-filter: blur(5px);
      padding: 0.5rem 1rem;
      color: var(--color-slate-800);
      text-align: center;
      a {
        color: var(--color-indigo-500);
      }
    }
  }

  .summary {
    display: none;
    @mixin screen md {
      display: block;
      @mixin typeface-summary;
    }
  }
}

/*
Controls
--------
*/
.controls {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: white;
  border-bottom: 1px solid var(--color-gray-100);
  overflow-x: scroll;
  overflow-y: visible;
  @mixin scrollbar-reset;
  @mixin screen md {
    padding: 0.5rem 0;
  }

  .creator-tag {
    position: absolute;
    top: $mapheightmobile;
    z-index: 100;
    right: 50%;
    transform: translateX(50%);
    @mixin screen md {
      visibility: hidden;
      position: absolute;
      top: auto;
      right: auto;
      transform: none;
      opacity: 0;
      transition: opacity 0.5s ease-in-out;
    }
  }
}

/*
Map view
--------
*/
$spotlistWidth: 30rem;
.map-view {
  overflow: hidden;
  @mixin screen sm {
    padding-left: calc($spotlistWidth - 1rem);
    border-radius: 1rem;
    position: relative;
    .spots-list {
      width: $spotlistWidth;
      position: absolute;
      border-radius: 0.75rem;
      top: 0rem;
      left: 0rem;
      z-index: 100;
    }
    .spot-wrapper {
      position: absolute !important;
      max-width: $spotlistWidth;
      height: calc(100vh - 7.5rem);
      top: 0;
      left: 0;
      z-index: 200;
    }
    .spot-wrapper:deep(.spot) {
      height: calc(100vh - 7.5rem) !important;
      border: 0;
    }
    .spot-wrapper:deep(.preview-overlay) {
      height: calc(100vh - 1rem) !important;
    }
  }
  @mixin screen xxl {
    padding-left: 0;
    .spots-list {
      top: 0.5rem;
      left: 0.5rem;
      height: calc(100vh - 7.5rem);
    }
    .spot-wrapper {
      max-width: 35rem;
      top: 0.5rem;
      left: 0.5rem;
      height: calc(100vh - 7.5rem);
      left: calc(100% - (35rem + 0.5rem));
      height: fit-content;
    }
  }
}

.map-container {
  z-index: 100;
}

/*
Spots
-------
*/
.spots-list {
  display: grid;
  padding: 0.5rem;
  @mixin screen md {
    box-shadow: var(--shadow-md);
    height: calc($mapheightdesktop);
  }
  h2 {
    display: none;
    @mixin screen md {
      display: block;
      @mixin typeface-summary;
      margin: 0.5rem 0.5rem 0rem;
      padding: 0 0 0.5rem;
      font-weight: 600;
      border-bottom: 1px solid var(--color-slate-50);
      span {
        font-weight: 800;
      }
    }
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    margin-bottom: 1rem;
    @mixin screen md {
      height: calc($mapheightdesktop - 7.5rem);
      border-radius: 0 0 0.5rem 0.5rem;
      overflow-y: scroll;
      padding: 0 0.5rem;
      @mixin scrollbar-reset;
    }
    @mixin screen xxl {
      height: calc($mapheightdesktop - 8rem);
    }
    li {
      margin: 0 auto;
      margin-bottom: 0.25rem;
      width: 100%;
      border-radius: 0.5rem;
      overflow: hidden;
      padding-bottom: 0.5rem;
      @mixin screen md {
        margin: 0;
      }
    }
  }
}

/*
Spot Indicator
--------------
*/
.spot-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: fit-content;
  margin: 0;
  padding: 0.5rem;
  display: inline-grid;
  gap: 0.25rem;
  list-style: none;
  grid-auto-flow: column;
  @mixin screen md {
    display: none;
  }
  ul {
    width: 100vw;
  }
  li {
    z-index: 100;
    background-color: rgba(255, 255, 255, 0.30);
    height: 0.25rem;
    min-width: 1px;
    border-radius: 0.25rem;
    position: relative;
    .indicator {
      position: absolute;
      top: 0;
      left: 0;
      height: 0.25rem;
      background-color: var(--main-background-color);
      border-radius: 0.25rem;
    }
    .indicator.active {
      animation: fill 7s ease-in-out;
      animation-fill-mode: forwards;
    }
    .indicator.paused {
      animation-play-state:paused;
    }
    .viewed {
      background-color: var(--main-background-color);
      width: 100%;
    }
  }
}

@keyframes fill {
  0% {
    width: 0;
  }
  100% {
    width: 100%;
  }
}

.unlock-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  padding: 1.5rem 1rem;
  border-radius: 0.5rem;
  background-color: var(--color-slate-100);
  z-index: 1;
  @mixin screen md {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
  }
  small {
    @mixin typeface-small;
    color: var(--color-slate-500);
    z-index: 1;
  }
  strong {
    @mixin typeface-h4;
    color: var(--color-slate-700);
    z-index: 1;
  }
  p {
    @mixin typeface-regular;
    color: var(--color-slate-500);
    z-index: 1;
  }
  & .bg-logo {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: -1;
    svg {
      path {
        fill: rgba(230, 230, 235, 0.35);
      }
    }
  }
}

.public-cta {
  background-color: var(--color-rose-500) !important;
  small {
    color: var(--color-white);
  }
  strong {
    color: var(--color-white);
  }
  p {
    color: var(--color-white);
  }

  & .bg-logo {
    svg {
      path {
        fill: rgba(230, 230, 235, 0.35);
      }
    }
  }
}

.authenticated-cta {
  background-color: var(--color-green-400) !important;
  small {
    color: var(--color-white);
  }
  strong {
    color: var(--color-white);
  }
  p {
    @mixin typeface-regular;
    color: var(--color-white);
  }

  .bg-logo {
    svg {
      path {
        fill: rgba(230, 230, 235, 0.35);
      }
    }
  }
}
</style>
